import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { API_URL, axios } from "../constants";

/**
 * Sticky FOMO ribbon for viral pages (Magic, Creators, AI Invitations, etc.).
 * - Self-fetches /api/promo/early-bird; renders nothing when inactive or
 *   slots are exhausted.
 * - One-tap CTA → /#book-online (cross-page navigation).
 * - Dismissible per-session via sessionStorage; reappears on next visit so
 *   we don't permanently lose the conversion lever.
 */
const SESSION_KEY = "dg_eb_strip_dismissed";

// ─────────────────────────────────────────────────────────────────────
// Launch offer temporarily disabled on founder's request (iter164).
// The default ₹1000-off strip was still showing on prod — this kill
// switch hides every surface that renders it (Magic, Creators,
// Invitations…). Flip to `false` to re-enable without touching backend.
// Kept at module scope so hooks still run in a stable order.
// ─────────────────────────────────────────────────────────────────────
const LAUNCH_OFFER_KILL_SWITCH = true;

const EarlyBirdStrip = () => {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (LAUNCH_OFFER_KILL_SWITCH) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/promo/early-bird`)
      .then(res => {
        if (cancelled) return;
        setPromo(res.data);
        if (res.data?.active && res.data?.slots_remaining > 0) {
          axios.post(`${API_URL}/api/promo/early-bird/event`, {
            kind: "impression",
            surface: "strip",
            page: window.location.pathname,
          }).catch(() => { /* fire and forget */ });
        }
      })
      .catch(() => { /* silent — strip simply won't render */ });
    return () => { cancelled = true; };
  }, []);

  if (LAUNCH_OFFER_KILL_SWITCH) return null;
  if (dismissed || !promo || !promo.active || !promo.slots_remaining) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleClick = () => {
    axios.post(`${API_URL}/api/promo/early-bird/event`, {
      kind: "click",
      surface: "strip",
      page: window.location.pathname,
    }).catch(() => { /* fire and forget */ });
  };

  return (
    <a
      href="/#book-online"
      onClick={handleClick}
      data-testid="early-bird-strip"
      className="block bg-gradient-to-r from-amber-500 via-rose-500 to-pink-600 text-white shadow-md"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/25 text-[12px] font-bold flex-shrink-0">★</span>
        <p className="flex-1 text-[13px] sm:text-sm font-medium leading-tight">
          <span className="font-bold">Launch offer</span>
          <span className="opacity-90"> · ₹{Number(promo.discount_inr).toLocaleString("en-IN")} OFF on your first booking</span>
          <span className="hidden sm:inline opacity-90"> · only </span>
          <span className="font-bold sm:ml-0 ml-1">{promo.slots_remaining} of {promo.total_slots} left</span>
        </p>
        <span className="hidden sm:inline-block text-[12px] font-bold bg-white text-rose-600 px-3 py-1 rounded-full whitespace-nowrap">
          Book now →
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss offer"
          data-testid="early-bird-strip-dismiss"
          className="flex-shrink-0 p-1 -mr-1 rounded-full hover:bg-white/15 transition"
        >
          <X size={16} />
        </button>
      </div>
    </a>
  );
};

export default EarlyBirdStrip;
